import React from 'react';
import * as mui from '@mui/material';
import { styled, alpha } from '@mui/material/styles';
import FileDownloadRoundedIcon from '@mui/icons-material/FileDownloadRounded';
import HistoryEduRoundedIcon from '@mui/icons-material/HistoryEduRounded';
import CopyAllRoundedIcon from '@mui/icons-material/CopyAllRounded';
import NormalTextField from '../../../common/NormalTextField';
import ModernSelect from '../../../common/ModernSelect';

// Modern minimalist styled components
const StyledCard = styled(mui.Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  marginBottom: theme.spacing(3),
  borderRadius: theme.spacing(1),
  boxShadow: 'none',
  border: '1px solid',
  borderColor: alpha(theme.palette.divider, 0.5),
  position: 'relative',
  overflow: 'hidden',
  '&:hover': {
    borderColor: alpha(theme.palette.primary.main, 0.3),
  },
}));

const SectionTitle = styled(mui.Typography)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  marginBottom: theme.spacing(2),
  fontWeight: 500,
  color: theme.palette.text.primary,
}));

const UserTerminationTab = ({
  selectedApps,
  terminationData,
  selectedNetReliance,
  selectedTermDoc,
  selectedTermNotifyer,
  selectedTermProcess,
  selectedDisableType,
  selectedTermDiff,
  YesNoOptions,
  networkrelianceOptions,
  handleOpenCopyModal,
  handleRelianceChange,
  handleTermDocChange,
  handleTermNotifyerChange,
  handleTermProcessChange,
  handleDisableTypeChange,
  handleTermedDiffChange,
  handleTermChange,
  handleTermDiffChange,
  handleTermBlur,
  handleTermDiffBlur,
}) => {
  return (
    <div>
      <mui.Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', textAlign: 'center' }}>
        <mui.Tooltip title="Copy Process" onClick={handleOpenCopyModal}>
          <mui.IconButton>
            <CopyAllRoundedIcon sx={{ width: 25, height: 25, color: '#046FB2' }} />
          </mui.IconButton>
        </mui.Tooltip>

        <mui.Tooltip title="Certify Accuracy">
          <mui.IconButton>
            <HistoryEduRoundedIcon sx={{ width: 25, height: 25, color: '#046FB2' }} />
          </mui.IconButton>
        </mui.Tooltip>

        <mui.Tooltip title="Download Process Narrative">
          <mui.IconButton>
            <FileDownloadRoundedIcon sx={{ width: 25, height: 25, color: '#046FB2' }} />
          </mui.IconButton>
        </mui.Tooltip>
      </mui.Box>

      <StyledCard>
        <SectionTitle variant="overline">Network Dependency</SectionTitle>
        <mui.Typography variant="subtitle2" sx={{ mb: 2 }}>
          Is access to {selectedApps.APP_NAME} dependent on network access?
        </mui.Typography>

        <div style={{ marginBottom: '18px' }}>
          <ModernSelect
            label="Network Dependency"
            isMultiSelect={false}
            placeholderText="Select Network Dependency"
            selectOptions={networkrelianceOptions}
            selectedOptions={Array.isArray(selectedNetReliance) && selectedNetReliance.length > 0 ? selectedNetReliance[0] : selectedNetReliance}
            handleChange={handleRelianceChange}
          />
        </div>
      </StyledCard>

      <StyledCard>
        <SectionTitle variant="overline">Termination Timeliness</SectionTitle>
        <mui.Typography variant="subtitle2" sx={{ mb: 2 }}>
          How many days are allowed to complete the termination process?
        </mui.Typography>
        <NormalTextField
          name="TIMELINESS"
          value={terminationData.TIMELINESS || ''}
          onChange={handleTermChange}
          onBlur={handleTermBlur}
          type="number"
          inputProps={{ min: 1 }}
          helperText="Number of days allowed to complete the termination process (minimum 1)"
        />
      </StyledCard>

      <StyledCard>
        <SectionTitle variant="overline">Termination Documentation</SectionTitle>
        <mui.Typography variant="subtitle2" sx={{ mb: 2 }}>
          How is the user termination documented?
        </mui.Typography>

        <div style={{ marginBottom: '18px' }}>
          <ModernSelect
            label="Documentation Form"
            isMultiSelect={true}
            placeholderText="Select Documentation Form"
            selectOptions={[
              { value: 'Email', label: 'Email' },
              { value: 'Form', label: 'Form' },
              { value: 'Ticket', label: 'Ticket' },
              { value: 'Other', label: 'Other' },
            ]}
            selectedOptions={selectedTermDoc}
            handleChange={handleTermDocChange}
          />
        </div>

        <mui.Typography variant="subtitle2" sx={{ mb: 1 }}>
          If 'Other' is selected in the question above, provide the documentation details here:
        </mui.Typography>
        <NormalTextField
          name="TERM_DOCUMENTATION_OTHER"
          value={terminationData.TERM_DOCUMENTATION_OTHER}
          onChange={handleTermChange}
          onBlur={handleTermBlur}
        />
      </StyledCard>

      <StyledCard>
        <SectionTitle variant="overline">Termination Process Description</SectionTitle>
        <mui.Typography variant="subtitle2" sx={{ mb: 2 }}>
          Describe the automated termination process, if applicable:
        </mui.Typography>
        <NormalTextField
          name="TERM_AUTOMATED_DESC"
          value={terminationData.TERM_AUTOMATED_DESC || ''}
          onChange={handleTermChange}
          onBlur={handleTermBlur}
          isMultiLine={true}
          rows={3}
          helperText="Describe any automation in the termination process, or type 'None' if not applicable"
        />
      </StyledCard>

      <StyledCard>
        <SectionTitle variant="overline">Termination Notification</SectionTitle>
        <mui.Typography variant="subtitle2" sx={{ mb: 2 }}>
          Who provides the notification that a user needs to be terminated from {selectedApps.APP_NAME}?
        </mui.Typography>

        <div style={{ marginBottom: '18px' }}>
          <ModernSelect
            label="Notifier"
            isMultiSelect={true}
            placeholderText="Select Notifier"
            selectOptions={[
              { value: 'Human Resources', label: 'Human Resources' },
              { value: 'Manager', label: 'Manager' },
              { value: 'Service Desk', label: 'Service Desk' },
              { value: 'System Admin', label: 'System Admin' },
              { value: 'Other', label: 'Other' },
            ]}
            selectedOptions={selectedTermNotifyer}
            handleChange={handleTermNotifyerChange}
          />
        </div>

        <mui.Typography variant="subtitle2" sx={{ mb: 1 }}>
          If 'Other' is selected in the question above, provide the notification source:
        </mui.Typography>
        <NormalTextField
          name="TERM_NOTIFYER_OTHER"
          value={terminationData.TERM_NOTIFYER_OTHER}
          onChange={handleTermChange}
          onBlur={handleTermBlur}
        />
      </StyledCard>

      <StyledCard>
        <SectionTitle variant="overline">Termination Process</SectionTitle>
        <mui.Typography variant="subtitle2" sx={{ mb: 2 }}>
          Who is responsible for performing the termination of access for the departed employee?
        </mui.Typography>

        <div style={{ marginBottom: '18px' }}>
          <ModernSelect
            label="Responsible Party"
            isMultiSelect={true}
            placeholderText="Select Responsible Party"
            selectOptions={[
              { value: 'Application Owner', label: 'Application Owner' },
              { value: 'Application Admin', label: 'Application Admin' },
              { value: 'Service Desk', label: 'Service Desk' },
              { value: 'Other', label: 'Other' },
            ]}
            selectedOptions={selectedTermProcess}
            handleChange={handleTermProcessChange}
          />
        </div>

        <mui.Typography variant="subtitle2" sx={{ mb: 1 }}>
          If 'Other' is selected in the question above, provide the responsible party:
        </mui.Typography>
        <NormalTextField
          name="TERM_PROCESS_OTHER"
          value={terminationData.TERM_PROCESS_OTHER}
          onChange={handleTermChange}
          onBlur={handleTermBlur}
        />
      </StyledCard>

      <StyledCard>
        <SectionTitle variant="overline">Disable Method</SectionTitle>
        <mui.Typography variant="subtitle2" sx={{ mb: 2 }}>
          How are accounts disabled for terminated users?
        </mui.Typography>

        <div style={{ marginBottom: '18px' }}>
          <ModernSelect
            label="Disable Method"
            isMultiSelect={true}
            placeholderText="Select Disable Method"
            selectOptions={[
              { value: 'Account Deletion', label: 'Account Deletion' },
              { value: 'Account Disabling', label: 'Account Disabling' },
              { value: 'Password Change', label: 'Password Change' },
              { value: 'Remove System Roles', label: 'Remove System Roles' },
              { value: 'Other', label: 'Other' },
            ]}
            selectedOptions={selectedDisableType}
            handleChange={handleDisableTypeChange}
          />
        </div>

        <mui.Typography variant="subtitle2" sx={{ mb: 1 }}>
          If 'Other' is selected in the question above, provide the disable method:
        </mui.Typography>
        <NormalTextField
          name="DISABLE_TYPE_OTHER"
          value={terminationData.DISABLE_TYPE_OTHER}
          onChange={handleTermChange}
          onBlur={handleTermBlur}
        />
      </StyledCard>

      <StyledCard>
        <SectionTitle variant="overline">Process Confirmation</SectionTitle>
        <mui.Typography variant="subtitle2" sx={{ mb: 2 }}>
          Does the termination process described above apply to temporary/contract based workers?
        </mui.Typography>

        <div style={{ marginBottom: '18px' }}>
          <ModernSelect
            label="Confirmation"
            isMultiSelect={false}
            placeholderText="Select Confirmation"
            selectOptions={YesNoOptions}
            selectedOptions={Array.isArray(selectedTermDiff) && selectedTermDiff.length > 0 ? selectedTermDiff[0] : selectedTermDiff}
            handleChange={handleTermedDiffChange}
          />
        </div>

        <mui.Typography variant="subtitle2" sx={{ mb: 1 }}>
          If your answer on the question above is 'No', briefly describe the termination process for temporary/contract based workers:
        </mui.Typography>
        <NormalTextField
          isMultiLine={true}
          rows="5"
          name="PROCESS_DIFFERENCE_OTHER"
          value={terminationData?.PROCESS_DIFFERENCE_OTHER}
          onChange={handleTermDiffChange}
          onBlur={handleTermDiffBlur}
        />
      </StyledCard>
    </div>
  );
};

export default UserTerminationTab; 